import isHotkey from 'is-hotkey'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { CirclePicker as Picker } from "react-color";
import { Dropdown } from "semantic-ui-react";
import { IconGroup } from "semantic-ui-react";
import { Popup } from "semantic-ui-react";
import { PlaceholderLine } from "semantic-ui-react";
import { Placeholder } from "semantic-ui-react";
import { Icon, Menu, Segment } from 'semantic-ui-react'
import { createEditor, Editor, Element as SlateElement, Transforms } from 'slate'
import { withHistory } from "slate-history";
import { Editable, ReactEditor, Slate, useSlate, withReact } from 'slate-react'
import EditorElement from "./EditorElement";
import EditorLeaf from "./EditorLeaf";
import { deserialize } from "./HtmlSerialisation.jsx";
import { serialize } from "./HtmlSerialisation.jsx";
import { MentionsButton } from "./Mentions.jsx";
import { withMentions } from "./Mentions.jsx";
import { TemplatesButton } from "./Templates";


const HOTKEYS = {
  'mod+b': 'bold',
  'mod+i': 'italic',
  'mod+u': 'underline',
  'mod+`': 'code',
}

const LIST_TYPES = ['numbered-list', 'bulleted-list']
const TEXT_ALIGN_TYPES = ['left', 'center', 'right', 'justify']

const RichTextEditor = (props) => {
  const renderElement = useCallback(props => <EditorElement {...props} />, [])
  const renderLeaf = useCallback(props => <EditorLeaf {...props} />, [])
  const editor = useMemo(() => withMentions(withHistory(withReact(createEditor()))), [])

  const html = props.serializeToHtml

  let initialValue = emptyInitialValue
  if(props.value){
    if(html){
      const document = new DOMParser().parseFromString(props.value, 'text/html')
      initialValue = deserialize(document.body)
    } else {
      initialValue = JSON.parse(props.value)
    }
  }

  if(props.loading) return (
    <Placeholder>
      <PlaceholderLine />
      <PlaceholderLine />
      <PlaceholderLine />
      <PlaceholderLine />
      <PlaceholderLine />
    </Placeholder>
  )

  return (
    //opgepast: breaking change als we Slate bumpen: value moet initialValue worden
    <Slate editor={editor} value={initialValue} onChange={
      value => {
        const serialized = html? serialize(value) : JSON.stringify(value)
        props.onChange(serialized)
      }
      }>
      <Menu icon attached='top'>
        {!props.simplified && <FontSizeButton/>}
        <MarkButton format="bold" icon="bold"/>
        <MarkButton format="italic" icon="italic"/>
        <MarkButton format="underline" icon="underline"/>
        <BlockButton format="heading-two" icon="header"/>
        <BlockButton format="block-quote" icon="quote right"/>
        <BlockButton format="numbered-list" icon="list ol"/>
        <BlockButton format="bulleted-list" icon="list ul"/>

        {!props.simplified && <React.Fragment>
        <ColorButton format="color" icon="font"/>
        {! props.textWillBeUsedInPdf && <ColorButton format="backgroundColor" icon="paint brush"/> /* prawnPDF can't do the background colors */  }
        <BlockButton format="left" icon="align left"/>
        <BlockButton format="center" icon="align center"/>
        <BlockButton format="right" icon="align right"/>
        <BlockButton format="justify" icon="align justify"/>
        </React.Fragment>}
        {props.mentionOptions && <MentionsButton mentionOptions={props.mentionOptions} editor={editor}/>}
        {props.useTemplates && <TemplatesButton useTemplates={props.useTemplates} editor={editor}/>}
      </Menu>
      <Segment attached='bottom' style={{ minHeight: '170px',  maxHeight: 'max(calc(82vh - 380px), 170px)', overflowY: 'auto' }}>
        <div className="selectable">
          <Editable
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            placeholder={I18n.t('components.placeholder.content')}
            spellCheck
            autoFocus
            onKeyDown={event => {
              for (const hotkey in HOTKEYS) {
                if (isHotkey(hotkey, event)) {
                  event.preventDefault()
                  const mark = HOTKEYS[hotkey]
                  toggleMark(editor, mark)
                }
              }
            }}
            onBlur={props.onBlur}
          />
        </div>
      </Segment>
    </Slate>
  )
}

const toggleBlock = (editor, format) => {
  const isActive = isBlockActive(
    editor,
    format,
    TEXT_ALIGN_TYPES.includes(format) ? 'align' : 'type'
  )
  const isList = LIST_TYPES.includes(format)

  Transforms.unwrapNodes(editor, {
    match: n =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      LIST_TYPES.includes(n.type) &&
      !TEXT_ALIGN_TYPES.includes(format),
    split: true,
  })
  let newProperties
  if (TEXT_ALIGN_TYPES.includes(format)) {
    newProperties = {
      align: isActive ? undefined : format,
    }
  } else {
    newProperties = {
      type: isActive ? 'paragraph' : isList ? 'list-item' : format,
    }
  }
  Transforms.setNodes(editor, newProperties)

  if (!isActive && isList) {
    const block = {type: format, children: []}
    Transforms.wrapNodes(editor, block)
  }
}

const toggleMark = (editor, format) => {
  const isActive = isMarkActive(editor, format)

  if (isActive) {
    Editor.removeMark(editor, format)
  } else {
    Editor.addMark(editor, format, true)
  }
}

const isBlockActive = (editor, format, blockType = 'type') => {
  const {selection} = editor
  if (!selection) return false

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: n =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        n[blockType] === format,
    })
  )

  return !!match
}

const isMarkActive = (editor, format) => {
  const marks = Editor.marks(editor)
  return marks ? marks[format] === true : false
}

const BlockButton = ({format, icon}) => {
  const editor = useSlate()
  return (
    <Menu.Item
      name={icon}
      active={isBlockActive(
        editor,
        format,
        TEXT_ALIGN_TYPES.includes(format) ? 'align' : 'type'
      )}
      onClick={event => {
        event.preventDefault()
        toggleBlock(editor, format)
        ReactEditor.focus(editor)
      }}
    >
      <Icon name={icon}/>
    </Menu.Item>
  )
}

const MarkButton = ({format, icon}) => {
  const editor = useSlate()
  return (
    <Menu.Item
      name={icon}
      active={isMarkActive(editor, format)}
      onClick={event => {
        event.preventDefault()
        toggleMark(editor, format)
        ReactEditor.focus(editor)
      }}
    >
      <Icon name={icon}/>
    </Menu.Item>
  )
}

const ColorButton = ({format, icon}) => {
  const editor = useSlate()
  let activeColor = (Editor.marks(editor) && Editor.marks(editor)[format]) ||  'black'
  const selectionRef = useRef(null)
  const [open, setOpen] = useState(false)

  return (
    <Popup
      open={open}
      onClose={() => setOpen(false)}
      onOpen={() => setOpen(true)}
      on="click"
      position="bottom center"
      trigger={
        <Menu.Item onClick={event => {
          event.preventDefault();
          selectionRef.current = editor.selection;
          setOpen(true)
        }}>
          <IconGroup>
            <Icon name={icon}/>
            <Icon name='tint' corner style={{ color: activeColor }} />
          </IconGroup>
        </Menu.Item>
      }
    >
      <Popup.Content>
        <Picker
          circleSize={15} circleSpacing={6}
          triangle="hide" colors={colors}
          onChangeComplete={color => {
            ReactEditor.focus(editor)
            if (selectionRef.current) {
              Transforms.select(editor, selectionRef.current)
            }
            Editor.addMark(editor, format, color.hex)
            setOpen(false)
          }}
        />
      </Popup.Content>
    </Popup>
  )
}

const FontSizeButton = () => {
  const editor = useSlate()
  const activeSize = Editor.marks(editor)?.fontSize?.replace("px","") || '14'

  return (
    <Dropdown
      item
      text={activeSize}
      options={fontSizes}
      selectOnBlur={false}
      onChange={(e, { value }) => {
        Editor.addMark(editor, 'fontSize', value)
      }}
      onClose={() => {
        ReactEditor.focus(editor)
      }}
    />
  )
}


const fontSizes = [
  { key: '12', text: '12', value: '12px' },
  { key: '14', text: '14', value: '14px' },
  { key: '16', text: '16', value: '16px' },
  { key: '18', text: '18', value: '18px' },
  { key: '24', text: '24', value: '24px' },
  { key: '32', text: '32', value: '32px' },
]

const colors = ['#4D4D4D', '#999999', '#FFFFFF', '#F44E3B', '#FE9200', '#FCDC00', '#DBDF00', '#A4DD00', '#68CCCA', '#73D8FF', '#AEA1FF', '#FDA1FF', '#333333', '#808080', '#cccccc', '#D33115', '#E27300', '#FCC400', '#B0BC00', '#68BC00', '#16A5A5', '#009CE0', '#7B64FF', '#FA28FF', '#000000', '#666666', '#B3B3B3', '#9F0500', '#C45100', '#FB9E00', '#808900', '#194D33', '#0C797D', '#0062B1', '#653294', '#AB149E']

const emptyInitialValue = [
  {
    type: "paragraph",
    children: [{ text: "" }],
  },
]

export default RichTextEditor
